import { useHeader, HeaderIconButton } from '@/v2/components/Header';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CloseIcon,
  LogoutIcon,
  AccountSettingIcon,
  PlansAndBillingIcon,
  UserIcon,
  WarningIcon,
  GuideIcon,
} from '@/v2/components/icons';
import { useAuth } from '@/commons/useAuth';
import { PATH } from '@/v2/utils/constants';
import {
  Button,
  Card,
  ConfigProvider,
  Flex,
  Modal,
  Space,
  Typography,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userInfoAtom } from '@/commons/utils/atoms';
import { useAtomValue } from 'jotai';

const HeaderLayout: React.FC = () => {
  const { logout } = useAuth();
  const userInfo = useAtomValue(userInfoAtom);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { headerContent, headerRightContent } = useHeader();

  const showLogoutModal = () => {
    setIsModalOpen(true);
    setIsDropdownOpen(false);
  };

  const handleCancel = () => setIsModalOpen(false);

  const handleConfirmLogout = () => {
    localStorage.clear();
    navigate(PATH.LOGIN);
    setIsModalOpen(false);
    logout();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <Flex
        justify="space-between"
        align="center"
        // Adjusted top spacing for better alignment with the design
        className="sticky top-0 z-10 backdrop-blur-[16px] !py-[9px] !px-4 bg-white/30 border border-white/50 rounded-full shadow-[0px_8px_16px_-8px_rgba(0,0,0,0.05)] transition-all duration-1000 ease-in-out !mt-0"
      >
        {headerContent}
        <Flex align="center" gap={4} className="!ml-auto">
          <Flex vertical align="end" ref={dropdownRef}>
            <Flex
              justify="space-between"
              align="center"
              className="relative z-20 w-[56px] h-[36px] bg-white/50 !rounded-[18px] !px-2 cursor-pointer  hover:bg-white/70"
              onClick={() => setIsDropdownOpen((prev) => !prev)}
            >
              <UserIcon />
              <Space className="relative z-30 transition-transform duration-1000 ease-in-out">
                <ArrowDownIcon />
              </Space>
            </Flex>

            {isDropdownOpen && (
              <Card
                className="!fixed w-[280px] !rounded-[18px] !z-20 !px-3 !py-3 shadow-[0px_8px_16px_8px_rgba(0,0,0,0.05)] border transition-all duration-300 ease-in-out opacity-100 transform scale-100"
                styles={{ body: { padding: 0 } }}
              >
                <Flex justify="space-between">
                  <Flex vertical className="py-1 ml-2 cursor-default">
                    <Typography.Text className="!text-[12px] !text-[#A5A4AA] !font-normal">
                      オーナーアカウント
                    </Typography.Text>
                    <Typography.Text className="!text-[16px] !font-bold text-[#333333]">
                      {userInfo?.user_name || 'User'} さん
                    </Typography.Text>
                  </Flex>
                  <Button
                    type="text"
                    shape="circle"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <ArrowUpIcon />
                  </Button>
                </Flex>

                <Flex
                  gap={2}
                  align="center"
                  className="!w-full !justify-start text-[14px] !font-normal rounded-[8px] !p-1 hover:!bg-[rgba(0,0,0,0.05)] hover:!cursor-pointer"
                  onClick={showLogoutModal}
                >
                  <LogoutIcon />
                  <span className="!text-[#FF5249] !font-normal">
                    ログアウト
                  </span>
                </Flex>

                <Flex
                  gap={2}
                  align="center"
                  className="!w-full !justify-start text-[14px] !font-normal rounded-[8px] !p-1 hover:!bg-[rgba(0,0,0,0.05)] hover:!cursor-pointer"
                  onClick={() => {
                    navigate(PATH.ACCOUNT_SETTING);
                    setIsDropdownOpen(false);
                  }}
                >
                  <AccountSettingIcon />
                  <span className="!text-[#6E5E83] !font-normal">
                    アカウント設定
                  </span>
                </Flex>

                <Flex
                  gap={2}
                  align="center"
                  className="!w-full !justify-start text-[14px] !font-normal rounded-[8px] !p-1 hover:!bg-[rgba(0,0,0,0.05)] hover:!cursor-pointer"
                  onClick={() => {
                    navigate(PATH.PLAN_AND_BILLING);
                    setIsDropdownOpen(false);
                  }}
                >
                  <PlansAndBillingIcon />
                  <span className="!text-[#6E5E83] !font-normal">
                    プラン＆お支払い
                  </span>
                </Flex>
              </Card>
            )}
          </Flex>
          <HeaderIconButton
            url="https://www.notion.so/PITALIy-2ca2487b188780e19877f5e2ad4c75c4"
            tooltipTitle="利用マニュアル"
            ariaLabel="利用マニュアル"
            icon={GuideIcon}
          />
          {headerRightContent}
        </Flex>
      </Flex>

      {/* Modal */}
      <ConfigProvider theme={{ token: { colorPrimary: '#C395EB' } }}>
        <Modal
          open={isModalOpen}
          onCancel={handleCancel}
          footer={null}
          centered
          closeIcon={<CloseIcon />}
          className="confirm-logout-modal modal-box-shadow"
        >
          <Flex vertical align="center" gap={24} className="!p-8">
            <Flex vertical align="center" gap={8}>
              <WarningIcon className="mb-2" />
              <Typography.Text className="!text-base !font-medium !text-[#333333]">
                ログアウトしますか？
              </Typography.Text>
            </Flex>
            <Flex justify="center" gap={8}>
              <Button
                onClick={handleCancel}
                className="!rounded-2xl !h-[45px] !w-[120px] hover:!bg-[#F2F2F2] hover:!border-[#A5A4AA] !text-[#A5A4AA] !border-[#A5A4AA] !leading-[150%]"
              >
                キャンセル
              </Button>
              <Button
                type="primary"
                danger
                onClick={handleConfirmLogout}
                className="!rounded-2xl !h-[45px] w-[120px] !p-3 hover:!bg-[#E54A42] !bg-[#FF5249] !leading-[150%]"
              >
                ログアウトする
              </Button>
            </Flex>
          </Flex>
        </Modal>
      </ConfigProvider>
    </>
  );
};

export default HeaderLayout;
